import React, { useCallback, useEffect, useRef, useState } from 'react';
import { limitToCents } from '../../lib/amount';
import invitationsService, {
  type BulkCommitResponse,
  type BulkPreviewResponse,
  type InvitationBatchDetail,
  type InvitationBatchSummary,
} from '../../services/invitations.service';
import authService from '../../services/auth.service';
import { tokens } from '../../components/forms/tokens';
import { getApiErrorMessage } from '../../lib/apiError';
import { formatDateTime } from '../../lib/datetime';
import { fullName } from '../../lib/fullName';
import PageHeader from '../../components/layout/PageHeader';
import InvitationLinks from '../../components/staff/InvitationLinks';

type LoadState = 'idle' | 'previewing' | 'previewed' | 'committing' | 'tracking' | 'error';

const BUCKET_LABELS: Record<string, string> = {
  new: 'New e-mails (an invitation will be sent)',
  expired_invitation: 'With expired invitations (will be sent again)',
  existing_user_active: 'Already active clients (skip)',
  existing_user_closed: '⚠ Closed accounts (skip + review)',
  already_pending: 'Pending lead (skip)',
  already_invited: 'Already invited (skip)',
  already_converted: 'Already clients (skip)',
  previously_rejected: '⚠ Previously rejected (skip)',
  internal_domain: 'Internal domain (skip)',
  duplicate_in_file: 'Duplicates in the file (skip)',
  unsubscribed: 'Unsubscribed from promotional e-mail (skip)',
};

const POLL_INTERVAL_MS = 4000;
const TERMINAL_STATUSES = new Set(['completed', 'failed']);

// Invitations are client-only by design. The map stays a Record<string>
// instead of a stricter type so older batches with `assigned_role=agent`
// (pre-2026-05-10 data) still render with a sensible label rather than
// crashing the table.
const ROLE_LABEL: Record<string, string> = {
  client: 'Client',
};

const STATUS_TONE: Record<string, { bg: string; text: string }> = {
  pending:  { bg: '#FEF3C7', text: '#92400E' },
  sent:     { bg: '#DBEAFE', text: '#1D4ED8' },
  accepted: { bg: '#D1FAE5', text: '#065F46' },
  failed:   { bg: '#FEE2E2', text: '#B91C1C' },
  skipped:  { bg: '#F3F4F6', text: '#374151' },
  expired:  { bg: '#F3F4F6', text: '#374151' },
  queued:     { bg: '#FEF3C7', text: '#92400E' },
  processing: { bg: '#DBEAFE', text: '#1D4ED8' },
  completed:  { bg: '#D1FAE5', text: '#065F46' },
};

export default function ManagerInvitations(): JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [state, setState] = useState<LoadState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<BulkPreviewResponse | null>(null);
  const [commitResult, setCommitResult] = useState<BulkCommitResponse | null>(null);
  const [detail, setDetail] = useState<InvitationBatchDetail | null>(null);
  const [recent, setRecent] = useState<InvitationBatchSummary[]>([]);

  // Vendor fields — required by the backend on commit. Free-form to
  // accommodate any vendor identifier the manager wants to record.
  const [vendorSource, setVendorSource] = useState('');
  const [vendorAcquiredAt, setVendorAcquiredAt] = useState('');
  const [vendorCost, setVendorCost] = useState('');
  // The basis the vendor states for having these contacts and a reference to its
  // evidence: without both no lead is created (#289).
  const [consentBasis, setConsentBasis] = useState('');
  const [consentEvidence, setConsentEvidence] = useState('');
  const consentGiven = consentBasis.trim().length >= 3 && consentEvidence.trim().length >= 5;

  const pollTimer = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const refreshRecent = useCallback(async () => {
    try {
      setRecent(await invitationsService.listBatches(10));
    } catch {
      // Silent — the recent panel is non-critical.
    }
  }, []);

  useEffect(() => {
    refreshRecent();
  }, [refreshRecent]);

  const stopPolling = useCallback(() => {
    if (pollTimer.current !== null) {
      globalThis.clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  const startPolling = useCallback(
    (batchId: string) => {
      stopPolling();
      const tick = async (): Promise<void> => {
        try {
          const next = await invitationsService.getBatch(batchId);
          setDetail(next);
          if (TERMINAL_STATUSES.has(next.batch.status)) {
            stopPolling();
            setState('idle');
            refreshRecent();
          }
        } catch (err) {
          stopPolling();
          setState('error');
          setError(getApiErrorMessage(err, 'We could not read the batch status.'));
        }
      };
      tick();
      pollTimer.current = globalThis.setInterval(tick, POLL_INTERVAL_MS);
    },
    [refreshRecent, stopPolling],
  );

  useEffect(() => () => stopPolling(), [stopPolling]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setError(null);
    setPreview(null);
    setCommitResult(null);
    setDetail(null);
    const f = e.target.files?.[0] ?? null;
    if (f && !f.name.toLowerCase().endsWith('.xlsx')) {
      setError('Only .xlsx files are accepted');
      setFile(null);
      return;
    }
    setFile(f);
  };

  // Step 1 — validate (dry-run). Reads the file but writes nothing.
  const handlePreview = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!file) return;
    setState('previewing');
    setError(null);
    try {
      const res = await invitationsService.previewBulk(file, {
        vendor_source: vendorSource.trim() || 'preview',  // not required on dry_run
        vendor_acquired_at: vendorAcquiredAt || undefined,
        vendor_cost: vendorCost.trim() || undefined,
      });
      setPreview(res);
      setState('previewed');
    } catch (err: unknown) {
      setState('error');
      setError(getApiErrorMessage(err, 'Preview failed'));
    }
  };

  // Step 2 — commit. Vendor source is REQUIRED here per the API.
  const handleCommit = async (): Promise<void> => {
    if (!file) return;
    if (!consentGiven) {
      setError('The consent basis and its evidence reference are required to commit.');
      return;
    }
    if (!vendorSource.trim()) {
      setError('Vendor source is required to commit (e.g. "BrokerX - Lista Q3 2026").');
      return;
    }
    setState('committing');
    setError(null);
    try {
      const res = await invitationsService.commitBulk(file, {
        vendor_source: vendorSource.trim(),
        vendor_acquired_at: vendorAcquiredAt || undefined,
        vendor_cost: vendorCost.trim() || undefined,
        consent_basis: consentBasis.trim(),
        consent_evidence_ref: consentEvidence.trim(),
      });
      setCommitResult(res);
      if (res.batch.total_count > 0) {
        setState('tracking');
        startPolling(res.batch.id);
      } else {
        setState('idle');
      }
      refreshRecent();
    } catch (err: unknown) {
      setState('error');
      setError(getApiErrorMessage(err, 'Commit failed'));
    }
  };

  const handleResetUpload = (): void => {
    setFile(null);
    setPreview(null);
    setCommitResult(null);
    setDetail(null);
    setError(null);
    setVendorSource('');
    setVendorAcquiredAt('');
    setVendorCost('');
    setState('idle');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDownloadCsv = async (batchId: string): Promise<void> => {
    const url = invitationsService.csvDownloadUrl(batchId);
    const token = authService.getToken();
    const res = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      setError('Could not download CSV');
      return;
    }
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `invitations_${batchId}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <>
      <PageHeader
        title="Bulk invitations"
        subtitle={
          <>
            Invite prospective <strong>clients</strong> in bulk. Upload an XLSX with columns:{' '}
            <span className="font-mono">email, first_name, last_name, role, language, notes</span>.
            Leave <span className="font-mono">role</span> blank or set to{' '}
            <span className="font-mono">client</span>; staff users are managed under{' '}
            <span className="font-mono">/admin/staff</span>.
          </>
        }
      />

      {/* ─── STEP 1 — Validate ──────────────────────────────────── */}
      <form
        onSubmit={handlePreview}
        className="rounded-2xl p-7 mb-8"
        style={{ background: tokens.surface, color: tokens.textPrimary }}
      >
        <h2 className="text-lg font-semibold mb-1">1. Validate file</h2>
        <p className="text-sm mb-4" style={{ color: tokens.textMuted }}>
          Max 1,000 rows · Max 2 MB · Vendor source recorded per row for ROI reports.
        </p>

        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          onChange={handleFileChange}
          disabled={state === 'previewing' || state === 'committing' || state === 'tracking'}
          className="block w-full text-sm mb-4 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:font-semibold"
          style={{ color: tokens.textPrimary }}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <label className="text-xs" style={{ color: tokens.textMuted }}>
            <span>Vendor / source</span>
            <input
              type="text" value={vendorSource}
              onChange={(e) => setVendorSource(e.target.value)}
              maxLength={200}
              placeholder="BrokerX - Lista Q3 2026"
              className="block w-full mt-1 px-3 py-2 rounded-lg text-sm"
              style={{ background: tokens.surfaceMuted, border: `1px solid ${tokens.border}`, color: tokens.textPrimary }}
            />
          </label>
          <label className="text-xs" style={{ color: tokens.textMuted }}>
            <span>Acquired date (optional)</span>
            <input
              type="date" value={vendorAcquiredAt}
              onChange={(e) => setVendorAcquiredAt(e.target.value)}
              className="block w-full mt-1 px-3 py-2 rounded-lg text-sm"
              style={{ background: tokens.surfaceMuted, border: `1px solid ${tokens.border}`, color: tokens.textPrimary }}
            />
          </label>
          <label className="text-xs" style={{ color: tokens.textMuted }}>
            <span>Cost paid (optional)</span>
            <input
              type="number" step="0.01" inputMode="decimal" min="0" value={vendorCost}
              onChange={(e) => setVendorCost(limitToCents(e.target.value))}
              placeholder="500.00"
              className="block w-full mt-1 px-3 py-2 rounded-lg text-sm"
              style={{ background: tokens.surfaceMuted, border: `1px solid ${tokens.border}`, color: tokens.textPrimary }}
            />
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
          <label className="text-xs" style={{ color: tokens.textMuted }}>
            <span>Consent basis the vendor states</span>
            <input
              type="text" value={consentBasis}
              onChange={(e) => setConsentBasis(e.target.value)}
              maxLength={120}
              placeholder="Opt-in consent collected by the vendor"
              className="block w-full mt-1 px-3 py-2 rounded-lg text-sm"
              style={{ background: tokens.surfaceMuted, border: `1px solid ${tokens.border}`, color: tokens.textPrimary }}
            />
          </label>
          <label className="text-xs" style={{ color: tokens.textMuted }}>
            <span>Evidence reference (contract number, consent-log id)</span>
            <input
              type="text" value={consentEvidence}
              onChange={(e) => setConsentEvidence(e.target.value)}
              maxLength={500}
              className="block w-full mt-1 px-3 py-2 rounded-lg text-sm"
              style={{ background: tokens.surfaceMuted, border: `1px solid ${tokens.border}`, color: tokens.textPrimary }}
            />
          </label>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg text-sm"
               style={{ background: tokens.errorBg, border: `1px solid ${tokens.errorBorder}`, color: tokens.errorText }}>
            {error}
          </div>
        )}

        <div className="flex gap-3">
          <button
            type="submit"
            disabled={!file || state === 'previewing' || state === 'committing' || state === 'tracking'}
            className="px-5 py-2.5 rounded-xl text-sm font-semibold"
            style={{
              background: file && (state === 'idle' || state === 'previewed')
                ? `linear-gradient(135deg, ${tokens.gold} 0%, ${tokens.goldLight} 100%)`
                : tokens.disabledBg,
              color: file ? tokens.navy : tokens.disabledText,
              cursor: file ? 'pointer' : 'not-allowed',
            }}
          >
            {state === 'previewing' ? 'Validating…' : 'Validate file'}
          </button>
          {(preview || commitResult || detail) && (
            <button
              type="button" onClick={handleResetUpload}
              className="px-5 py-2.5 rounded-xl text-sm font-semibold"
              style={{ background: tokens.surfaceMuted, color: tokens.textPrimary }}
            >
              New batch
            </button>
          )}
        </div>
      </form>

      {/* ─── STEP 2 — Preview summary + confirm ─────────────────── */}
      {preview && state !== 'tracking' && !commitResult && (
        <div className="rounded-2xl p-7 mb-8"
             style={{ background: tokens.surface, color: tokens.textPrimary }}>
          <h2 className="text-lg font-semibold mb-1">2. Review summary</h2>
          <p className="text-sm mb-4" style={{ color: tokens.textMuted }}>
            File format: <strong>{preview.format_ok ? '✓ correct' : '✗ has parse errors'}</strong> ·{' '}
            Total rows: <strong>{preview.total_rows}</strong>
          </p>

          <ul className="text-sm space-y-2 mb-5">
            {Object.entries(preview.buckets).map(([bucket, count]) => {
              const label = BUCKET_LABELS[bucket] ?? bucket;
              const isWarning = bucket === 'existing_user_closed' || bucket === 'previously_rejected';
              return (
                <li key={bucket} className="flex items-center justify-between p-2 rounded-lg"
                    style={{
                      background: isWarning ? tokens.errorBg : tokens.surfaceMuted,
                      color: isWarning ? tokens.errorText : tokens.textPrimary,
                    }}>
                  <span>{label}</span>
                  <strong>{count}</strong>
                </li>
              );
            })}
          </ul>

          {preview.parse_errors.length > 0 && (
            <div className="rounded-lg p-3 mb-4 text-sm"
                 style={{ background: tokens.errorBg, color: tokens.errorText, border: `1px solid ${tokens.errorBorder}` }}>
              <strong>Parse errors ({preview.parse_errors.length})</strong>
              <ul className="mt-2 space-y-1">
                {preview.parse_errors.slice(0, 10).map((er, i) => (
                  <li key={`${er.row_number}-${i}`}>
                    Row {er.row_number}{er.field ? ` · ${er.field}` : ''}: {er.message}
                  </li>
                ))}
                {preview.parse_errors.length > 10 && (
                  <li className="italic">…and {preview.parse_errors.length - 10} more.</li>
                )}
              </ul>
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleCommit}
              disabled={state === 'committing' || !vendorSource.trim() || !consentGiven}
              className="px-5 py-2.5 rounded-xl text-sm font-semibold"
              style={{
                background: vendorSource.trim()
                  ? `linear-gradient(135deg, ${tokens.gold} 0%, ${tokens.goldLight} 100%)`
                  : tokens.disabledBg,
                color: vendorSource.trim() ? tokens.navy : tokens.disabledText,
                cursor: vendorSource.trim() ? 'pointer' : 'not-allowed',
              }}
            >
              {state === 'committing' ? 'Sending…' : `Confirm & send (${preview.buckets.new ?? 0} new + ${preview.buckets.expired_invitation ?? 0} re-send)`}
            </button>
            {!vendorSource.trim() && (
              <span className="text-xs self-center" style={{ color: tokens.errorText }}>
                Vendor source required to commit.
              </span>
            )}
            {!consentGiven && (
              <span className="text-xs self-center" style={{ color: tokens.errorText }}>
                Consent basis and evidence reference required to commit.
              </span>
            )}
          </div>
        </div>
      )}

      {commitResult && (
        <InvitationLinks links={commitResult.links.map((l) => ({ who: l.email, link: l.link }))} />
      )}

      {/* ─── Post-commit compliance warnings ─────────────────────── */}
      {commitResult && commitResult.compliance_warnings.length > 0 && (
        <div className="rounded-2xl p-6 mb-8"
             style={{ background: tokens.errorBg, color: tokens.errorText, border: `2px solid ${tokens.errorBorder}` }}>
          <h3 className="font-semibold mb-2">
            ⚠ Compliance warnings ({commitResult.compliance_warnings.length})
          </h3>
          <p className="text-sm mb-3">
            These rows were flagged automatically — review with compliance.
          </p>
          <ul className="text-sm space-y-1">
            {commitResult.compliance_warnings.map((w, i) => (
              <li key={`${w.kind}-${w.row_number}-${i}`}>
                <strong>Row {w.row_number}</strong> · {w.email_masked} · <em>{w.kind}</em> — {w.message}
              </li>
            ))}
          </ul>
        </div>
      )}

        {/* Live batch detail */}
        {detail && (
          <div className="rounded-2xl p-7 mb-8"
               style={{ background: tokens.surface, color: tokens.textPrimary }}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold">Batch</h2>
                <p className="font-mono text-xs" style={{ color: tokens.textMuted }}>{detail.batch.id}</p>
              </div>
              <span className="px-3 py-1 rounded-full text-xs font-semibold"
                    style={{
                      background: STATUS_TONE[detail.batch.status]?.bg ?? tokens.surfaceMuted,
                      color: STATUS_TONE[detail.batch.status]?.text ?? tokens.textPrimary,
                    }}>
                {detail.batch.status}
              </span>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
              <Stat label="Total" value={detail.batch.total_count} />
              <Stat label="Sent" value={detail.batch.sent_count} />
              <Stat label="Accepted" value={detail.batch.accepted_count} />
              <Stat label="Skipped" value={detail.batch.skipped_count} />
              <Stat label="Failed" value={detail.batch.failed_count} />
            </div>

            <button type="button"
                    onClick={() => handleDownloadCsv(detail.batch.id)}
                    className="text-sm font-medium mb-4"
                    style={{ color: tokens.infoText }}>
              ↓ Download results CSV
            </button>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ color: tokens.textMuted, borderBottom: `1px solid ${tokens.border}` }}>
                    <th className="text-left py-2 pr-3">Email</th>
                    <th className="text-left py-2 pr-3">Name</th>
                    <th className="text-left py-2 pr-3">Role</th>
                    <th className="text-left py-2 pr-3">Status</th>
                    <th className="text-left py-2 pr-3">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.items.map((it) => (
                    <tr key={it.id} style={{ borderBottom: `1px solid ${tokens.border}` }}>
                      <td className="py-2 pr-3 font-mono text-xs">{it.email}</td>
                      <td className="py-2 pr-3">{fullName(it)}</td>
                      <td className="py-2 pr-3">{ROLE_LABEL[it.assigned_role] ?? it.assigned_role}</td>
                      <td className="py-2 pr-3">
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
                              style={{
                                background: STATUS_TONE[it.status]?.bg ?? tokens.surfaceMuted,
                                color: STATUS_TONE[it.status]?.text ?? tokens.textPrimary,
                              }}>
                          {it.status}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-xs" style={{ color: tokens.textMuted }}>
                        {it.skip_reason ?? it.send_error ?? ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Recent batches */}
        {recent.length > 0 && (
          <div className="rounded-2xl p-7"
               style={{ background: tokens.surface, color: tokens.textPrimary }}>
            <h2 className="text-lg font-semibold mb-4">Recent batches</h2>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ color: tokens.textMuted, borderBottom: `1px solid ${tokens.border}` }}>
                  <th className="text-left py-2 pr-3">Created</th>
                  <th className="text-left py-2 pr-3">File</th>
                  <th className="text-left py-2 pr-3">Status</th>
                  <th className="text-right py-2 pr-3">Total</th>
                  <th className="text-right py-2 pr-3">Sent</th>
                  <th className="text-right py-2 pr-3">Failed</th>
                  <th className="text-right py-2 pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {recent.map((b) => (
                  <tr key={b.id} style={{ borderBottom: `1px solid ${tokens.border}` }}>
                    <td className="py-2 pr-3 text-xs">
                      {formatDateTime(b.created_at)}
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs">{b.file_name ?? '—'}</td>
                    <td className="py-2 pr-3">
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold"
                            style={{
                              background: STATUS_TONE[b.status]?.bg ?? tokens.surfaceMuted,
                              color: STATUS_TONE[b.status]?.text ?? tokens.textPrimary,
                            }}>
                        {b.status}
                      </span>
                    </td>
                    <td className="py-2 pr-3 text-right">{b.total_count}</td>
                    <td className="py-2 pr-3 text-right">{b.sent_count}</td>
                    <td className="py-2 pr-3 text-right">{b.failed_count}</td>
                    <td className="py-2 pr-3 text-right">
                      <button type="button"
                              onClick={() => startPolling(b.id)}
                              className="text-xs font-medium"
                              style={{ color: tokens.infoText }}>
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </>
  );
}

function Stat({ label, value }: Readonly<{ label: string; value: number }>): JSX.Element {
  return (
    <div className="rounded-xl p-3" style={{ background: tokens.surfaceMuted }}>
      <div className="text-xs" style={{ color: tokens.textMuted }}>{label}</div>
      <div className="text-2xl font-bold" style={{ color: tokens.textPrimary }}>{value}</div>
    </div>
  );
}
